import React, { useEffect, useState } from 'react';
import http from '../../api/http';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Box, IconButton, Typography } from '@mui/material';

interface ReferenceRange {
  min: number;
  max: number;
  unit: string;
}

interface ConversionCoefficients {
  [key: string]: number;
}

interface BiomarkerDefinition {
  _id: number;
  nameEn: string;
  synonyms: string[];
  unitPreference: string;
  referenceRange: ReferenceRange;
  conversionCoefficients: ConversionCoefficients;
  checkupTypes: string[];
  indicatorId?: number;
  createdAt: string;
  updatedAt: string;
}

interface CreateDefinitionData {
  nameEn: string;
  synonyms: string[];
  unitPreference: string;
  conversionCoefficients: ConversionCoefficients;
  referenceRange: ReferenceRange;
  checkupTypes: string[];
  indicatorId?: number;
}

interface ApiResponse {
  success: boolean;
  data: BiomarkerDefinition[];
}

const Definitions: React.FC = () => {
  const [definitions, setDefinitions] = useState<BiomarkerDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedDefinition, setSelectedDefinition] = useState<BiomarkerDefinition | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateDefinitionData>({
    nameEn: '',
    synonyms: [''],
    unitPreference: '',
    conversionCoefficients: {},
    referenceRange: {
      min: 0,
      max: 0,
      unit: ''
    },
    checkupTypes: [''],
    indicatorId: undefined
  });

  const fetchDefinitions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await http.get('/api/lab-results/definitions');
      setDefinitions(response.data.data || []);
    } catch (err: any) {
      const status = err.response?.status;
      let errorMessage = 'Error fetching definitions';
      
      switch (status) {
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'Insufficient permissions. Admin access required.';
          break;
        default:
          errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Error fetching definitions';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSynonymChange = (index: number, value: string) => {
    const newSynonyms = [...formData.synonyms];
    newSynonyms[index] = value;
    setFormData(prev => ({
      ...prev,
      synonyms: newSynonyms
    }));
  };

  const addSynonym = () => {
    setFormData(prev => ({
      ...prev,
      synonyms: [...prev.synonyms, '']
    }));
  };

  const removeSynonym = (index: number) => {
    if (formData.synonyms.length > 1) {
      const newSynonyms = formData.synonyms.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        synonyms: newSynonyms
      }));
    }
  };

  const handleCheckupTypeChange = (index: number, value: string) => {
    const newCheckupTypes = [...formData.checkupTypes];
    newCheckupTypes[index] = value;
    setFormData(prev => ({
      ...prev,
      checkupTypes: newCheckupTypes
    }));
  };

  const addCheckupType = () => {
    setFormData(prev => ({
      ...prev,
      checkupTypes: [...prev.checkupTypes, '']
    }));
  };

  const removeCheckupType = (index: number) => {
    if (formData.checkupTypes.length > 1) {
      const newCheckupTypes = formData.checkupTypes.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        checkupTypes: newCheckupTypes
      }));
    }
  };

  const handleConversionCoefficientChange = (unit: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      conversionCoefficients: {
        ...prev.conversionCoefficients,
        [unit]: numValue
      }
    }));
  };

  const addConversionCoefficient = () => {
    const newUnit = prompt('Enter unit (e.g., mg/dL):');
    if (newUnit && !formData.conversionCoefficients[newUnit]) {
      setFormData(prev => ({
        ...prev,
        conversionCoefficients: {
          ...prev.conversionCoefficients,
          [newUnit]: 0
        }
      }));
    }
  };

  const removeConversionCoefficient = (unit: string) => {
    const newCoefficients = { ...formData.conversionCoefficients };
    delete newCoefficients[unit];
    setFormData(prev => ({
      ...prev,
      conversionCoefficients: newCoefficients
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nameEn.trim() || !formData.unitPreference.trim()) {
      setCreateError('Name and unit are required');
      return;
    }

    try {
      setCreating(true);
      setCreateError(null);
      
      const payload = {
        ...formData,
        synonyms: formData.synonyms.filter(s => s.trim()),
        checkupTypes: formData.checkupTypes.filter(c => c.trim()),
        conversionCoefficients: Object.fromEntries(
          Object.entries(formData.conversionCoefficients).filter(([_, value]) => value !== 0)
        )
      };

      const response = await http.post('/api/lab-results/definitions', payload);
      
      if (response.data.success) {
        setIsModalOpen(false);
        setFormData({
          nameEn: '',
          synonyms: [''],
          unitPreference: '',
          conversionCoefficients: {},
          referenceRange: { min: 0, max: 0, unit: '' },
          checkupTypes: [''],
          indicatorId: undefined
        });
        await fetchDefinitions();
      }
    } catch (err: any) {
      const status = err.response?.status;
      let errorMessage = 'Error creating definition';
      
      switch (status) {
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'Insufficient permissions. Admin access required.';
          break;
        case 400:
          errorMessage = err.response?.data?.message || err.response?.data?.error || 'Invalid data provided';
          break;
        default:
          errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Error creating definition';
      }
      
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedDefinition || !formData.nameEn.trim()) {
      setEditError('Name is required');
      return;
    }

    try {
      setEditing(true);
      setEditError(null);
      
      const payload = {
        ...formData,
        synonyms: formData.synonyms.filter(s => s.trim()),
        checkupTypes: formData.checkupTypes.filter(c => c.trim()),
        conversionCoefficients: Object.fromEntries(
          Object.entries(formData.conversionCoefficients).filter(([_, value]) => value !== 0)
        )
      };

      const response = await http.put(`/api/lab-results/definitions/${selectedDefinition._id}`, payload);
      
      if (response.data.success) {
        setIsEditModalOpen(false);
        setSelectedDefinition(null);
        resetForm();
        await fetchDefinitions();
      }
    } catch (err: any) {
      const status = err.response?.status;
      let errorMessage = 'Error updating definition';
      
      switch (status) {
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'Insufficient permissions. Admin access required.';
          break;
        case 404:
          errorMessage = 'Definition not found or invalid ID.';
          break;
        default:
          errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Error updating definition';
      }
      
      setEditError(errorMessage);
    } finally {
      setEditing(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDefinition) {
      setDeleteError('No definition selected for deletion');
      return;
    }

    try {
      setDeleting(true);
      setDeleteError(null);

      const response = await http.remove(`/api/lab-results/definitions/${selectedDefinition._id}`, {});
      
      if (response.data.success) {
        setIsDeleteModalOpen(false);
        setSelectedDefinition(null);
        await fetchDefinitions();
      }
    } catch (err: any) {
      const status = err.response?.status;
      let errorMessage = 'Error deleting definition';
      
      switch (status) {
        case 401:
          errorMessage = 'Unauthorized. Please log in again.';
          break;
        case 403:
          errorMessage = 'Insufficient permissions. Admin access required.';
          break;
        case 404:
          errorMessage = 'Definition not found or invalid ID.';
          break;
        default:
          errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Error deleting definition';
      }
      
      setDeleteError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nameEn: '',
      synonyms: [''],
      unitPreference: '',
      conversionCoefficients: {},
      referenceRange: { min: 0, max: 0, unit: '' },
      checkupTypes: [''],
      indicatorId: undefined
    });
    setCreateError(null);
  };

  const handleRowClick = (definition: BiomarkerDefinition) => {
    setSelectedDefinition(definition);
    setIsDetailModalOpen(true);
  };

  const handleDeleteDefinitionClick = (definition: BiomarkerDefinition) => {
    setSelectedDefinition(definition);
    setIsDeleteModalOpen(true);
  };

  const handleEditClick = (definition: BiomarkerDefinition) => {
    setFormData({
      nameEn: definition.nameEn,
      synonyms: definition.synonyms.length > 0 ? definition.synonyms : [''],
      unitPreference: definition.unitPreference,
      conversionCoefficients: { ...definition.conversionCoefficients },
      referenceRange: { ...definition.referenceRange },
      checkupTypes: definition.checkupTypes.length > 0 ? definition.checkupTypes : [''],
      indicatorId: definition.indicatorId
    });
    setSelectedDefinition(definition);
    setIsDetailModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = () => {
    setIsDetailModalOpen(false);
    setIsDeleteModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading definitions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading</h3>
              <p className="mt-2 text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between align-items-center gap-10">
                <h1 className="text-2xl font-bold text-gray-900">Biomarker Definitions</h1>
                <button 
                  className="btn"
                  onClick={() => setIsModalOpen(true)}
                >
                  Add New Definition
                </button>
              </div>
            </div>
            
            <div className="px-6 py-6">
              {definitions.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No definitions</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by creating a new biomarker definition.</p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-blue-700">
                            Total definitions: <span className="font-semibold">{definitions.length}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Synonyms</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Checkup Types</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {definitions.map((definition) => (
                          <tr 
                            key={definition._id} 
                            className="hover:bg-gray-50"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {definition.nameEn}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="flex flex-nowrap gap-1 overflow-hidden">
                                {definition.synonyms.slice(0, 3).map((synonym, index) => (
                                  <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {synonym}
                                  </span>
                                ))}
                                {definition.synonyms.length > 3 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    +{definition.synonyms.length - 3}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="flex flex-nowrap gap-1 overflow-hidden">
                                {(definition.checkupTypes ?? []).length > 0 ? (
                                  (definition.checkupTypes ?? []).slice(0, 3).map((type, index) => (
                                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      {type}
                                    </span>
                                  ))
                                ) : (
                                  '-'
                                )}
                                {(definition.checkupTypes ?? []).length > 3 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                    +{(definition.checkupTypes ?? []).length - 3}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 flex gap-2 whitespace-nowrap text-sm font-medium">
                              <button className="btn text-teal-600 hover:text-teal-900 mr-4" onClick={() => handleEditClick(definition)}>Edit</button>
                              <button className="btn px-1 text-teal-600 hover:text-teal-900" onClick={() => handleDeleteDefinitionClick(definition)}>Delete</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Definition Modal */}
      <Dialog 
        open={isModalOpen} 
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Biomarker Definition</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: '100vw', sm: 600 } }}>
          {createError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                label="Name"
                type="text"
                fullWidth
                value={formData.nameEn}
                onChange={(e) => handleInputChange('nameEn', e.target.value)}
                placeholder="e.g., Glucose"
                required
              />
              <TextField
                margin="dense"
                label="Preferred"
                type="text"
                fullWidth
                value={formData.unitPreference}
                onChange={(e) => handleInputChange('unitPreference', e.target.value)}
                placeholder="e.g., mmol/L"
                required
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <strong>Synonyms</strong>
                </Box>
                <Button size="small" onClick={addSynonym}>
                  + Add Synonym
                </Button>
              </Box>
              {formData.synonyms.map((synonym, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={synonym}
                    onChange={(e) => handleSynonymChange(index, e.target.value)}
                    placeholder="e.g., GLU, Blood Sugar"
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  {formData.synonyms.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeSynonym(index)}
                      color="error"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <strong>Conversion Coefficients</strong>
                </Box>
                <Button size="small" onClick={addConversionCoefficient}>
                  + Add Coefficient
                </Button>
              </Box>
              {Object.entries(formData.conversionCoefficients).map(([unit, coefficient]) => (
                <Box key={unit} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ width: 80, mr: 1 }}>
                    <TextField
                      size="small"
                      value={unit}
                      disabled
                      sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                    />
                  </Box>
                  <TextField
                    size="small"
                    type="number"
                    value={coefficient}
                    onChange={(e) => handleConversionCoefficientChange(unit, e.target.value)}
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeConversionCoefficient(unit)}
                    color="error"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </IconButton>
                </Box>
              ))}
            </Box>

            <Box sx={{ mb: 2 }}>
              <strong>Reference Range</strong>
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <TextField
                  size="small"
                  label="Min"
                  type="number"
                  value={formData.referenceRange.min}
                  onChange={(e) => handleInputChange('referenceRange', {
                    ...formData.referenceRange,
                    min: parseFloat(e.target.value) || 0
                  })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Max"
                  type="number"
                  value={formData.referenceRange.max}
                  onChange={(e) => handleInputChange('referenceRange', {
                    ...formData.referenceRange,
                    max: parseFloat(e.target.value) || 0
                  })}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Unit"
                  type="text"
                  value={formData.referenceRange.unit}
                  onChange={(e) => handleInputChange('referenceRange', {
                    ...formData.referenceRange,
                    unit: e.target.value
                  })}
                  placeholder="e.g., mmol/L"
                  fullWidth
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <strong>Checkup Types</strong>
                </Box>
                <Button size="small" onClick={addCheckupType}>
                  + Add Type
                </Button>
              </Box>
              {formData.checkupTypes.map((checkupType, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={checkupType}
                    onChange={(e) => handleCheckupTypeChange(index, e.target.value)}
                    placeholder="e.g., BasicCheckup"
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  {formData.checkupTypes.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeCheckupType(index)}
                      color="error"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            <TextField
              margin="dense"
              label="Indicator ID (optional)"
              type="number"
              fullWidth
              value={formData.indicatorId || ''}
              onChange={(e) => handleInputChange('indicatorId', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="e.g., 123"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setIsModalOpen(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            disabled={creating}
          >
            {creating ? 'Creating...' : 'Create Definition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail View Modal */}
      <Dialog 
        open={isDetailModalOpen} 
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedDefinition(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Definition Details</span>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => selectedDefinition && handleEditClick(selectedDefinition)}
                sx={{ color: '#14b8a6', borderColor: '#14b8a6', '&:hover': { backgroundColor: 'rgba(20,184,166,0.04)', borderColor: '#0d9488' } }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={handleDeleteClick}
                sx={{ color: '#14b8a6', borderColor: '#14b8a6', '&:hover': { backgroundColor: 'rgba(20,184,166,0.04)', borderColor: '#0d9488' } }}
              >
                Delete
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDefinition && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="Name"
                    value={selectedDefinition.nameEn}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Preferred Unit"
                    value={selectedDefinition.unitPreference}
                    fullWidth
                    disabled
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="ID"
                    value={selectedDefinition._id}
                    fullWidth
                    disabled
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    label="Indicator ID"
                    value={selectedDefinition.indicatorId || 'Not specified'}
                    fullWidth
                    disabled
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <strong>Synonyms</strong>
                  <Box sx={{ ml: 2, color: 'text.secondary' }}>
                    ({selectedDefinition.synonyms.length})
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDefinition.synonyms.map((synonym, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'primary.50',
                        color: 'primary.700',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {synonym}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <strong>Conversion Coefficients</strong>
                  <Box sx={{ ml: 2, color: 'text.secondary' }}>
                    ({Object.keys(selectedDefinition.conversionCoefficients || {}).length})
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Object.entries(selectedDefinition.conversionCoefficients || {}).map(([unit, coefficient]) => (
                    <Box
                      key={unit}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ fontWeight: 500, minWidth: 80 }}>{unit}:</Box>
                      <Box sx={{ fontFamily: 'monospace' }}>{coefficient}</Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <strong>Reference Range</strong>
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField
                    label="Min"
                    value={selectedDefinition.referenceRange?.min || 'Not specified'}
                    disabled
                    fullWidth
                  />
                  <TextField
                    label="Max"
                    value={selectedDefinition.referenceRange?.max || 'Not specified'}
                    disabled
                    fullWidth
                  />
                  <TextField
                    label="Unit"
                    value={selectedDefinition.referenceRange?.unit || 'Not specified'}
                    disabled
                    fullWidth
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <strong>Checkup Types</strong>
                  <Box sx={{ ml: 2, color: 'text.secondary' }}>
                    ({selectedDefinition.checkupTypes.length})
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedDefinition.checkupTypes.map((checkupType, index) => (
                    <Box
                      key={index}
                      sx={{
                        px: 2,
                        py: 1,
                        bgcolor: 'success.50',
                        color: 'success.700',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}
                    >
                      {checkupType}
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <strong>Created:</strong>
                  <Box sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {new Date(selectedDefinition.createdAt).toLocaleString()}
                  </Box>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <strong>Last Updated:</strong>
                  <Box sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {new Date(selectedDefinition.updatedAt).toLocaleString()}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setIsDetailModalOpen(false);
              setSelectedDefinition(null);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Definition Modal */}
      <Dialog 
        open={isEditModalOpen} 
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedDefinition(null);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Biomarker Definition</DialogTitle>
        <DialogContent sx={{ minWidth: { xs: '100vw', sm: 600 } }}>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleUpdate} sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                autoFocus
                margin="dense"
                label="Name *"
                type="text"
                fullWidth
                value={formData.nameEn}
                onChange={(e) => handleInputChange('nameEn', e.target.value)}
                placeholder="e.g., Glucose"
                required
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <strong>Synonyms</strong>
                </Box>
                <Button size="small" onClick={addSynonym}>
                  + Add Synonym
                </Button>
              </Box>
              {formData.synonyms.map((synonym, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={synonym}
                    onChange={(e) => handleSynonymChange(index, e.target.value)}
                    placeholder="e.g., GLU, Blood Sugar"
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  {formData.synonyms.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeSynonym(index)}
                      color="error"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <strong>Checkup Types</strong>
                </Box>
                <Button size="small" onClick={addCheckupType}>
                  + Add Type
                </Button>
              </Box>
              {formData.checkupTypes.map((checkupType, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TextField
                    size="small"
                    value={checkupType}
                    onChange={(e) => handleCheckupTypeChange(index, e.target.value)}
                    placeholder="e.g., BasicCheckup"
                    sx={{ flexGrow: 1, mr: 1 }}
                  />
                  {formData.checkupTypes.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeCheckupType(index)}
                      color="error"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>

            <TextField
              margin="dense"
              label="Indicator ID (optional)"
              type="number"
              fullWidth
              value={formData.indicatorId || ''}
              onChange={(e) => handleInputChange('indicatorId', e.target.value ? parseInt(e.target.value) : undefined)}
              placeholder="e.g., 123"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setIsEditModalOpen(false);
              setSelectedDefinition(null);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleUpdate}
            variant="contained"
            disabled={editing}
          >
            {editing ? 'Updating...' : 'Update Definition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog 
        open={isDeleteModalOpen} 
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedDefinition(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          
          <Box sx={{ mt: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete the definition <strong>"{selectedDefinition?.nameEn}"</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This action cannot be undone. The definition will be permanently removed from the system.
            </Typography>
            {selectedDefinition && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Definition details:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • ID: {selectedDefinition._id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Unit: {selectedDefinition.unitPreference}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Synonyms: {selectedDefinition.synonyms.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Checkup Types: {selectedDefinition.checkupTypes.length}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setIsDeleteModalOpen(false);
              setSelectedDefinition(null);
            }}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete}
            variant="contained"
            disabled={deleting}
            sx={{ backgroundColor: '#14b8a6', '&:hover': { backgroundColor: '#0d9488' } }}
          >
            {deleting ? 'Deleting...' : 'Delete Definition'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Definitions;
